import React, { useCallback, useEffect, useRef, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import axios from "axios";
import { ReflectorOption } from "./ReflectorSelector";

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";

function getPlaceholderText(
  loading: boolean,
  error: boolean,
  availableCount: number,
  selectedCount: number,
  fallback: string
): string {
  if (loading) return "Loading reflectors…";
  if (error) return "Error loading reflectors";
  if (availableCount === 0 && selectedCount > 0) return "All reflectors selected";
  return fallback;
}

async function fetchAdminReflectors(token: string): Promise<ReflectorOption[]> {
  const { data } = await axios.get<
    { subId: string; name: string; tenant?: { Name?: string; nameDisplay?: string } }[]
  >(`${API_BASE_URL}/v2/reflector-admin/instances`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return (data ?? []).map((r) => ({
    subId: r.subId ?? "",
    name: r.name ?? "",
    tenantName: r.tenant?.nameDisplay ?? r.tenant?.Name ?? "",
  }));
}

interface Props {
  /** Currently selected UUIDs (subIds). */
  value: string[];
  onChange: (subIds: string[]) => void;
  id?: string;
  placeholder?: string;
  disabled?: boolean;
}

/**
 * Multi-select for reflectors — Admin Center version.
 * Loads ALL reflectors across every tenant (no tenant filter).
 */
const ReflectorMultiSelector: React.FC<Props> = ({
  value,
  onChange,
  id = "reflector-multi-selector",
  placeholder = "Add a reflector…",
  disabled = false,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const getTokenRef = useRef(getAccessTokenSilently);
  getTokenRef.current = getAccessTokenSilently;

  const [options, setOptions] = useState<ReflectorOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const token = await getTokenRef.current();
      const mapped = await fetchAdminReflectors(token);
      setOptions(mapped);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleAdd = (subId: string) => {
    if (!subId || value.includes(subId)) return;
    onChange([...value, subId]);
  };

  const handleRemove = (subId: string) => {
    onChange(value.filter((v) => v !== subId));
  };

  const available = options.filter((o) => !value.includes(o.subId));
  const selectedOptions = options.filter((o) => value.includes(o.subId));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{ position: "relative" }}>
        <select
          id={id}
          value=""
          onChange={(e) => handleAdd(e.target.value)}
          disabled={disabled || loading || available.length === 0}
          style={{
            width: "100%",
            padding: "6px 32px 6px 10px",
            border: `1px solid ${error ? "#fca5a5" : "#d1d5db"}`,
            borderRadius: 6,
            fontSize: 13,
            fontFamily: "monospace",
            background: disabled || loading ? "#f9fafb" : "#fff",
            color: "#9ca3af",
            appearance: "none",
            cursor: disabled || loading ? "not-allowed" : "pointer",
          }}
        >
          <option value="">
            {getPlaceholderText(loading, error, available.length, value.length, placeholder)}
          </option>
          {available.map((opt) => (
            <option key={opt.subId} value={opt.subId}>
              {opt.name} — {opt.subId} ({opt.tenantName})
            </option>
          ))}
        </select>
        <span
          style={{
            position: "absolute",
            right: 10,
            top: "50%",
            transform: "translateY(-50%)",
            pointerEvents: "none",
            color: "#6b7280",
            fontSize: 12,
          }}
        >
          ▾
        </span>
      </div>

      {value.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {value.map((subId) => {
            const opt = selectedOptions.find((o) => o.subId === subId);
            const label = opt ? `${opt.name} (${subId.slice(0, 8)}…)` : subId;
            return (
              <span
                key={subId}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                  padding: "3px 10px",
                  borderRadius: 20,
                  background: "#dbeafe",
                  border: "1px solid #93c5fd",
                  fontSize: 12,
                  fontFamily: "monospace",
                  color: "#1e40af",
                }}
              >
                {label}
                {!disabled && (
                  <button
                    type="button"
                    onClick={() => handleRemove(subId)}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      padding: 0,
                      lineHeight: 1,
                      color: "#3b82f6",
                      fontSize: 14,
                      fontWeight: 700,
                    }}
                    aria-label={`Remove ${label}`}
                  >
                    ×
                  </button>
                )}
              </span>
            );
          })}
        </div>
      )}

      {value.length === 0 && (
        <p style={{ fontSize: 11, color: "#9ca3af", margin: 0 }}>
          No reflectors selected — config scope will be used.
        </p>
      )}
    </div>
  );
};

export default ReflectorMultiSelector;
